import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Collapse,
  Card,
  Typography,
  Divider,
  Button,
  Breadcrumb,
  Result,
  Spin,
} from "antd";
import { fetchAllApplicationsDetails } from "../actions/applicationactions";
import { selectTestbed } from "../slices/testbedSlice";
import "./ApplicationDetails.css";

const { Panel } = Collapse;
const { Title, Text } = Typography;

const ApplicationDetails = () => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const { applications, status, error } = useSelector(
    (state) => state.applications
  );
  const tbId = useSelector(selectTestbed); // 'tbA' | 'tbB'
  const isTMA = tbId === "tbB";

  const [showAnalysis, setShowAnalysis] = useState(false);
  const [analysisImageUrl, setAnalysisImageUrl] = useState(null);

  const application = Array.isArray(applications)
    ? applications.find((app) => String(app.applicationId) === String(applicationId))
    : null;

  const parseManifest = (manifestFile) => {
    if (!manifestFile || !manifestFile.data) return "";
    return String.fromCharCode(...manifestFile.data);
  };

  useEffect(() => {
    if (!applications || applications.length === 0) {
      dispatch(fetchAllApplicationsDetails());
    }
  }, [applications, dispatch]);

  useEffect(() => {
    if (
      !isTMA &&
      showAnalysis &&
      application &&
      application.analysisResult &&
      application.analysisResult.file &&
      application.analysisResult.file.data
    ) {
      const uint8Arr = new Uint8Array(application.analysisResult.file.data);
      const mimeType = `image/${application.analysisResult.fileExtension}`;
      const blob = new Blob([uint8Arr], { type: mimeType });
      const url = URL.createObjectURL(blob);
      setAnalysisImageUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [showAnalysis, application, isTMA]);

  let content;

  if (status === "loading" || status === "idle") {
    content = (
      <div className="app-details-center">
        <Spin size="large" />
        <p className="app-details-loading-text">Loading application details…</p>
      </div>
    );
  } else if (status === "failed" || error) {
    content = (
      <div className="app-details-center">
        <Result
          status="error"
          title="Could not load application details"
          subTitle={
            <>
              We were unable to retrieve the application details.
              <br />
              The backend may be temporarily unavailable.
            </>
          }
          extra={
            <Button type="primary" onClick={() => dispatch(fetchAllApplicationsDetails())}>
              Retry
            </Button>
          }
        >
          {error && (
            <div className="error-details">
              <span style={{ fontSize: 12, opacity: 0.7 }}>
                Technical details: {String(error)}
              </span>
            </div>
          )}
        </Result>
      </div>
    );
  } else if (!application) {
    content = (
      <div className="app-details-center">
        <Result
          status="404"
          title="Application not found"
          subTitle="The requested application does not exist or may have been deleted."
          extra={
            <Button type="primary">
              <Link to="/applications">Back to Application Overview</Link>
            </Button>
          }
        />
      </div>
    );
  } else {
    content = (
      <Card className="application-card">
        <Title className="title" level={2}>
          Application Details
        </Title>

        <div className="detail-item">
          <Text strong>Application Name:</Text>{" "}
          <Text>{application.applicationName}</Text>
        </div>

        <Divider />

        {application.components && application.components.length > 0 && (
          <div className="components-section">
            <Title className="title" level={4}>
              Components
            </Title>
            <Collapse accordion>
              {application.components.map((component) => (
                <Panel header={component.name} key={component.id}>
                  <div className="component-detail">
                    <div className="detail-item">
                      <Text strong>Component ID:</Text>{" "}
                      <Text>{component.id}</Text>
                    </div>

                    {component.jobs && component.jobs.length > 0 && (
                      <Collapse className="jobs-collapse">
                        <Panel header="Jobs" key="jobs">
                          {component.jobs.map((job) => (
                            <Card
                              key={job.id}
                              className="job-card"
                              type="inner"
                              title={job.serviceName}
                            >
                              <div className="detail-item">
                                <Text strong>Job ID:</Text>{" "}
                                <Text>{job.id}</Text>
                              </div>
                              <div className="detail-item">
                                <Text strong>Manifest Name:</Text>{" "}
                                <Text>{job.manifestName}</Text>
                              </div>
                              <div className="detail-item">
                                <Text strong>Execution Time:</Text>{" "}
                                <Text>{job.executionTime}</Text>
                              </div>
                              <div className="detail-item">
                                <Text strong>Frequency:</Text>{" "}
                                <Text>{job.frequency}</Text>
                              </div>
                              <div className="detail-item">
                                <Text strong>CPU:</Text>{" "}
                                <Text>{job.cpu}</Text>
                              </div>
                              <div className="detail-item">
                                <Text strong>Memory:</Text>{" "}
                                <Text>{job.memory}</Text>
                              </div>
                              <div className="manifest-section">
                                <Text strong>Manifest File:</Text>
                                <pre className="yaml-code">
                                  {parseManifest(job.manifestFile)}
                                </pre>
                              </div>
                            </Card>
                          ))}
                        </Panel>
                      </Collapse>
                    )}
                  </div>
                </Panel>
              ))}
            </Collapse>
          </div>
        )}

        {application.channels && application.channels.length > 0 && (
          <>
            <Divider />
            <div className="channels-section">
              <Title level={4}>Channels</Title>
              <Collapse>
                <Panel header="Channels List" key="channels">
                  {application.channels.map((channel) => (
                    <Card key={channel.id} className="channel-card" type="inner">
                      <div className="detail-item">
                        <Text strong>Channel ID:</Text>{" "}
                        <Text>{channel.id}</Text>
                      </div>
                      <div className="detail-item">
                        <Text strong>Incoming Component ID:</Text>{" "}
                        <Text>{channel.incomingComponentId}</Text>
                      </div>
                      <div className="detail-item">
                        <Text strong>Outgoing Component ID:</Text>{" "}
                        <Text>{channel.outgoingComponentId}</Text>
                      </div>
                    </Card>
                  ))}
                </Panel>
              </Collapse>
            </div>
          </>
        )}

        {/* Analysis block only for TB-A */}
        {!isTMA && showAnalysis && application.analysisResult && (
          <>
            <Divider />
            <div className="analysis-section">
              <Title level={4}>Analysis Results</Title>
              <div className="detail-item">
                <Text strong>Consistency:</Text>{" "}
                <Text>{String(application.analysisResult.consistency)}</Text>
              </div>
              <div className="detail-item">
                <Text strong>Liveness:</Text>{" "}
                <Text>{String(application.analysisResult.liveness)}</Text>
              </div>
              <div className="detail-item">
                <Text strong>File Name:</Text>{" "}
                <Text>{application.analysisResult.fileName}</Text>
              </div>
              <div className="detail-item">
                <Text strong>File Extension:</Text>{" "}
                <Text>{application.analysisResult.fileExtension}</Text>
              </div>
              <div className="image-preview">
                {analysisImageUrl ? (
                  <img
                    src={analysisImageUrl}
                    alt="Analysis Result"
                    style={{ maxWidth: "100%" }}
                  />
                ) : (
                  <Text>Loading image...</Text>
                )}
              </div>
            </div>
          </>
        )}

        {!isTMA && !showAnalysis && (
          <>
            <Divider />
            <div className="card-bottom-actions">
              <Button
                type="default"
                className="analysis-btn"
                onClick={() => setShowAnalysis(true)}
              >
                View Analysis Results
              </Button>
            </div>
          </>
        )}
      </Card>
    );
  }

  return (
    <>
      {/* Breadcrumb + header */}
      <div className="page-header">
        <div className="page-breadcrumb-wrapper">
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/home">Home</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/applications">Application Overview</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Application Details</Breadcrumb.Item>
          </Breadcrumb>

        </div>
      </div>

      <div className="application-details-container">{content}</div>
    </>
  );
};

export default ApplicationDetails;
